import {computed, Injectable, signal} from '@angular/core';
import {jwtDecode} from 'jwt-decode';
import {Jwt} from '../../shared/models/Jwt.model';
import {JwtToken} from '../../shared/models/JwtToken';

const TOKEN_KEY = 'auth-token';
const EXPIRES_AT_KEY = 'auth-expires-at';
const REFRESH_AFTER_KEY = 'auth-refresh-after';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly _token = signal<string | null>(localStorage.getItem(TOKEN_KEY));
  private readonly _expiresAt = signal<string | null>(localStorage.getItem(EXPIRES_AT_KEY));
  private readonly _refreshAfter = signal<string | null>(localStorage.getItem(REFRESH_AFTER_KEY));

  readonly token = this._token.asReadonly();
  readonly expiresAt = this._expiresAt.asReadonly();
  readonly refreshAfter = this._refreshAfter.asReadonly();

  readonly decodedToken = computed<Jwt | null>(() => {
    const raw = this._token();
    if (!raw) return null;
    try {
      return jwtDecode<Jwt>(raw);
    } catch {
      return null;
    }
  });

  saveJwtToken(jwtToken: JwtToken) {
    localStorage.setItem(TOKEN_KEY, jwtToken.token);
    localStorage.setItem(EXPIRES_AT_KEY, jwtToken.expiresAt);
    localStorage.setItem(REFRESH_AFTER_KEY, jwtToken.refreshAfter);
    this._token.set(jwtToken.token);
    this._expiresAt.set(jwtToken.expiresAt);
    this._refreshAfter.set(jwtToken.refreshAfter);
  }

  destroyJwtToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    localStorage.removeItem(REFRESH_AFTER_KEY);
    this._token.set(null);
    this._expiresAt.set(null);
    this._refreshAfter.set(null);
  }

  needsRefresh(): boolean {
    const raw = this._refreshAfter();
    if (!raw) return false;
    return new Date() >= new Date(raw);
  }

  isExpired(): boolean {
    const raw = this._expiresAt();
    if (!raw) return true;
    return new Date() >= new Date(raw);
  }
}
